import React, { useState } from 'react';
import {
    AlertTriangle, ArrowRight, Check, CheckCircle2, Clock, Plus, RotateCcw, Trash2, X,
} from 'lucide-react';
import { readOnlyTitle } from '../../services/readOnlyNotice';
import { SCHWERE_LABEL } from './gewerkeKatalog';
import type {
    AbgleichZeile, GespeicherteBewertung, KoordinierungPaar, Prioritaet, Vorschlag,
} from './koordinierungLogic';

/**
 * Ein Gewerkepaar im Einzelnen — reine Anzeige und Knopfweitergabe.
 *
 * Links die bereits gespeicherten Bewertungen, rechts das, was der Katalog
 * vorschlaegt. Keine Rechnung, keine Datenbank; das Projekt hat kein jsdom,
 * pruefbar ist nur, was in `koordinierungLogic.ts` steht.
 *
 * ═══ EIN VORSCHLAG IST EIN VORSCHLAG ═════════════════════════════════════
 * Nichts auf dieser Seite bestaetigt sich selbst. Ein uebernommener Vorschlag
 * ist eine gespeicherte Zeile OHNE `bestaetigt_am` — sichtbar als „noch nicht
 * bestätigt". Erst der ausdrueckliche Klick eines Menschen macht daraus die
 * Feststellung des Koordinators, und erst die steht spaeter im PDF.
 */

interface Props {
    zeile: AbgleichZeile;
    isReadOnly: boolean;
    laeuft: boolean;
    /** Migration Nr. 40 fehlt noch — dann ist jeder Schreibweg zwecklos. */
    tabelleFehlt: boolean;
    onSchliessen: () => void;
    onUebernehmen: (zeile: AbgleichZeile, vorschlag: Vorschlag) => void;
    onEigene: (zeile: AbgleichZeile, gefaehrdung: string) => void;
    onBestaetigen: (paar: KoordinierungPaar) => void;
    onWiderrufen: (paar: KoordinierungPaar) => void;
    onLoeschen: (paar: KoordinierungPaar) => void;
    onAendern: (paar: KoordinierungPaar, updates: Partial<KoordinierungPaar>) => void;
    onFensterUebernehmen: (bewertung: GespeicherteBewertung) => void;
}

const PRIO_KLASSE: Record<Prioritaet, string> = {
    hoch: 'bg-rose-50 text-rose-700 border-rose-200',
    mittel: 'bg-amber-50 text-amber-800 border-amber-200',
    niedrig: 'bg-slate-50 text-slate-600 border-slate-200',
};

const PRIO_LABEL: Record<Prioritaet, string> = {
    hoch: 'Hoch',
    mittel: 'Mittel',
    niedrig: 'Gering',
};

const QUELLE_LABEL: Record<KoordinierungPaar['quelle'], string> = {
    regel: 'aus dem Katalog',
    ki: 'KI-Vorschlag',
    hand: 'von Hand',
};

const tagText = (iso: string | null): string =>
    typeof iso === 'string' && iso.length >= 10
        ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}`
        : '—';

export const PaarDetail: React.FC<Props> = ({
    zeile, isReadOnly, laeuft, tabelleFehlt, onSchliessen, onUebernehmen, onEigene,
    onBestaetigen, onWiderrufen, onLoeschen, onAendern, onFensterUebernehmen,
}) => {
    const [eigene, setEigene] = useState('');
    const gesperrt = isReadOnly || laeuft || tabelleFehlt;
    const sperrGrund = tabelleFehlt
        ? 'Die Ablage für Bewertungen fehlt noch (Migration Nr. 40).'
        : isReadOnly ? readOnlyTitle('Bewertungen') : undefined;

    return (
        <div className="bg-white rounded-2xl border border-slate-300 shadow-sm">

            {/* ─── Kopf ───────────────────────────────────────────────── */}
            <div className="flex items-start gap-3 px-5 py-4 border-b border-slate-100">
                <div className="min-w-0 flex-1">
                    <h3 className="text-base font-black text-slate-900 leading-tight">
                        {zeile.aLabel} <span className="text-slate-300 mx-1">×</span> {zeile.bLabel}
                    </h3>
                    <p className="text-xs text-slate-500 mt-0.5">
                        {zeile.berechnet ? (
                            <>
                                Gemeinsam vom {tagText(zeile.berechnet.von)} bis {tagText(zeile.berechnet.bis)}
                                {' · '}{zeile.berechnet.dauerTage} Tag(e)
                            </>
                        ) : (
                            <>Der aktuelle Plan ergibt keine gemeinsame Zeit mehr.</>
                        )}
                    </p>
                </div>
                <button
                    type="button"
                    onClick={onSchliessen}
                    className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 shrink-0"
                    aria-label="Schließen"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>

            {zeile.status === 'veraltet' && (
                <div className="mx-5 mt-4 bg-slate-100 border border-slate-200 rounded-xl px-4 py-3 text-xs text-slate-700 flex items-start gap-2">
                    <Clock className="w-4 h-4 shrink-0 mt-0.5 text-slate-400" />
                    <span>
                        Diese Bewertung ist überholt: der Plan sieht die beiden Gewerke nicht mehr
                        gleichzeitig vor. Sie bleibt stehen und wird nur gekennzeichnet — dass die
                        Gefährdung einmal erkannt und geregelt war, gehört in die Akte.
                    </span>
                </div>
            )}

            {/* ─── Gespeicherte Bewertungen ───────────────────────────── */}
            <div className="px-5 py-4 space-y-3">
                <p className="text-[11px] font-black text-slate-400 uppercase tracking-wide">
                    Bewertungen ({zeile.bewertungen.length})
                </p>

                {zeile.bewertungen.length === 0 && (
                    <p className="text-sm text-slate-400">
                        Noch nichts bewertet. Übernehmen Sie einen Vorschlag oder tragen Sie eine eigene
                        Gefährdung ein.
                    </p>
                )}

                {zeile.bewertungen.map(b => (
                    <div
                        key={b.zeile.id}
                        className={`rounded-xl border p-3.5 ${b.zeile.bestaetigt_am ? 'border-emerald-200 bg-emerald-50/40' : 'border-slate-200'}`}
                    >
                        <div className="flex items-start gap-2 mb-2 flex-wrap">
                            <span className={`text-[10px] font-black uppercase tracking-wide border rounded px-1.5 py-0.5 ${PRIO_KLASSE[b.zeile.prioritaet]}`}>
                                {PRIO_LABEL[b.zeile.prioritaet]}
                            </span>
                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">
                                {QUELLE_LABEL[b.zeile.quelle]}
                                {b.zeile.katalog_version && ` · Fassung ${b.zeile.katalog_version}`}
                            </span>
                            {b.zeile.bestaetigt_am ? (
                                <span className="ml-auto inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700">
                                    <CheckCircle2 className="w-3.5 h-3.5" />
                                    bestätigt {tagText(b.zeile.bestaetigt_am.slice(0, 10))}
                                    {b.zeile.bestaetigt_von_name && ` · ${b.zeile.bestaetigt_von_name}`}
                                </span>
                            ) : (
                                <span className="ml-auto text-[11px] font-bold text-slate-400">
                                    noch nicht bestätigt
                                </span>
                            )}
                        </div>

                        <p className="text-sm text-slate-800 leading-snug">{b.zeile.gefaehrdung}</p>
                        {b.zeile.massnahme && (
                            <p className="text-xs text-slate-600 leading-snug mt-1.5">
                                <span className="font-bold">Maßnahme: </span>{b.zeile.massnahme}
                            </p>
                        )}

                        {/* Alt/Neu bei verschobenem Zeitraum. Überschrieben wird
                            nichts automatisch: bewertet wurde der ALTE Zeitraum. */}
                        {b.befund === 'geaendert' && b.neu && (
                            <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-xs text-blue-900">
                                <p className="font-bold mb-1">Der Zeitraum hat sich verschoben.</p>
                                {/* Auch eine als überholt gekennzeichnete Zeile landet hier,
                                    wenn die Überschneidung mit einem ANDEREN Fenster
                                    zurückkommt. Ohne diesen Satz sähe es aus, als sei die
                                    alte Bewertung wieder gültig. */}
                                {b.zeile.veraltet_seit && (
                                    <p className="mb-1">
                                        Diese Bewertung war am {tagText(b.zeile.veraltet_seit.slice(0, 10))} als
                                        überholt gekennzeichnet. Der Plan führt die beiden Gewerke wieder
                                        gleichzeitig — aber in einem anderen Zeitraum als dem bewerteten.
                                    </p>
                                )}
                                <p className="flex items-center gap-2 flex-wrap">
                                    <span>bewertet: {tagText(b.alt.von)} – {tagText(b.alt.bis)}
                                        {b.alt.dauerTage != null && ` (${b.alt.dauerTage} Tage)`}</span>
                                    <ArrowRight className="w-3.5 h-3.5" />
                                    <span className="font-bold">jetzt: {tagText(b.neu.von)} – {tagText(b.neu.bis)} ({b.neu.dauerTage} Tage)</span>
                                </p>
                                <button
                                    type="button"
                                    onClick={() => onFensterUebernehmen(b)}
                                    disabled={gesperrt}
                                    title={sperrGrund}
                                    className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-bold text-blue-700 bg-white border border-blue-300 rounded-lg hover:bg-blue-50 disabled:opacity-40"
                                >
                                    Neuen Zeitraum übernehmen
                                </button>
                                <p className="mt-1.5 text-[11px] text-blue-800/80">
                                    Die Bestätigung fällt dabei weg — bestätigt wurde ein anderer Zeitraum.
                                </p>
                            </div>
                        )}

                        {b.befund === 'wieder_aktuell' && (
                            <p className="mt-2 text-xs text-emerald-700">
                                War als überholt gekennzeichnet und überschneidet sich wieder.
                            </p>
                        )}
                        {b.zeile.veraltet_seit && b.befund === 'veraltet' && (
                            <p className="mt-2 text-xs text-slate-500">
                                Als überholt gekennzeichnet am {tagText(b.zeile.veraltet_seit.slice(0, 10))}.
                            </p>
                        )}

                        {/* ─── Knöpfe ─────────────────────────────────── */}
                        <div className="mt-3 pt-2.5 border-t border-slate-100 flex items-center gap-2 flex-wrap">
                            {b.zeile.bestaetigt_am ? (
                                <button
                                    type="button"
                                    onClick={() => onWiderrufen(b.zeile)}
                                    disabled={gesperrt}
                                    title={sperrGrund}
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-bold text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-40"
                                >
                                    <RotateCcw className="w-3.5 h-3.5" /> Bestätigung zurücknehmen
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => onBestaetigen(b.zeile)}
                                    disabled={gesperrt}
                                    title={sperrGrund}
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-bold text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-40"
                                >
                                    <Check className="w-3.5 h-3.5" /> Bestätigen
                                </button>
                            )}

                            <select
                                value={b.zeile.prioritaet}
                                disabled={gesperrt}
                                onChange={e => onAendern(b.zeile, { prioritaet: e.target.value as Prioritaet })}
                                title={sperrGrund || 'Priorität'}
                                className="text-[11px] font-bold border border-slate-300 rounded-lg px-2 py-1 bg-white disabled:opacity-40"
                            >
                                <option value="hoch">Hoch</option>
                                <option value="mittel">Mittel</option>
                                <option value="niedrig">Gering</option>
                            </select>

                            {!b.zeile.bestaetigt_am && (
                                <button
                                    type="button"
                                    onClick={() => onLoeschen(b.zeile)}
                                    disabled={gesperrt}
                                    title={sperrGrund || 'Nur unbestätigte Bewertungen lassen sich löschen.'}
                                    className="ml-auto inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-bold text-rose-600 hover:bg-rose-50 rounded-lg disabled:opacity-40"
                                >
                                    <Trash2 className="w-3.5 h-3.5" /> Löschen
                                </button>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {/* ─── Vorschläge ─────────────────────────────────────────── */}
            <div className="px-5 py-4 border-t border-slate-100 bg-slate-50/70 rounded-b-2xl space-y-3">
                <p className="text-[11px] font-black text-slate-400 uppercase tracking-wide">
                    Vorschläge aus dem Katalog ({zeile.vorschlaege.length})
                </p>

                {zeile.vorschlaege.length === 0 && (
                    <p className="text-sm text-slate-400">
                        Der Katalog kennt zu diesem Paar keine Regel. Das ist kein Nachweis der
                        Gefährdungsfreiheit — tragen Sie gegebenenfalls eine eigene Gefährdung ein.
                    </p>
                )}

                {zeile.vorschlaege.map(v => (
                    <div key={v.code} className="bg-white rounded-xl border border-slate-200 p-3.5">
                        <div className="flex items-start gap-2 mb-2 flex-wrap">
                            <span className="text-[10px] font-black text-slate-400 tracking-wide">{v.code}</span>
                            <span className={`text-[10px] font-black uppercase tracking-wide border rounded px-1.5 py-0.5 ${PRIO_KLASSE[v.prioritaet]}`}>
                                {SCHWERE_LABEL[v.schwere]}
                            </span>
                            {v.ausgehendVon && (
                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">
                                    geht aus von: {v.ausgehendVon}
                                </span>
                            )}
                            {v.bereitsGespeichert && (
                                <span className="ml-auto text-[11px] font-bold text-emerald-700">bereits übernommen</span>
                            )}
                        </div>

                        <p className="text-sm text-slate-800 leading-snug">{v.gefaehrdung}</p>
                        <p className="text-xs text-slate-600 leading-snug mt-1.5">
                            <span className="font-bold">Maßnahme: </span>{v.massnahme}
                        </p>
                        {v.vorschriften.length > 0 && (
                            <p className="text-[11px] text-slate-400 mt-1.5 italic">
                                {v.vorschriften.join(' · ')}
                            </p>
                        )}

                        {/* ═══ RÜCKFRAGE RAUMBEZUG ══════════════════════════
                            `raeumliche_zuordnung` ist Freitext. Zwei solche Texte
                            maschinell auf „übereinander" oder „nicht übereinander"
                            abzubilden hieße raten — und ein geratenes Nein löscht
                            eine echte Gefährdung, ohne dass es jemand merkt. */}
                        {v.raumfrage && (
                            <div className="mt-2.5 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-start gap-2">
                                <AlertTriangle className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />
                                <p className="text-[11px] text-amber-900 leading-snug">{v.raumfrage}</p>
                            </div>
                        )}

                        <button
                            type="button"
                            onClick={() => onUebernehmen(zeile, v)}
                            disabled={gesperrt || v.bereitsGespeichert}
                            title={sperrGrund}
                            className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white bg-slate-800 rounded-lg hover:bg-slate-900 disabled:opacity-40"
                        >
                            <Plus className="w-3.5 h-3.5" /> Als Bewertung übernehmen
                        </button>
                    </div>
                ))}

                {/* ─── Eigene Gefährdung ──────────────────────────────── */}
                <div className="bg-white rounded-xl border border-dashed border-slate-300 p-3.5">
                    <label className="block text-[11px] font-black text-slate-400 uppercase tracking-wide mb-1.5">
                        Eigene Gefährdung
                    </label>
                    <div className="flex items-start gap-2 flex-wrap">
                        <input
                            type="text"
                            value={eigene}
                            disabled={gesperrt}
                            onChange={e => setEigene(e.target.value)}
                            placeholder="Was passiert konkret, wenn beide gleichzeitig arbeiten?"
                            className="flex-1 min-w-[16rem] text-sm border border-slate-300 rounded-lg px-3 py-1.5 disabled:opacity-40"
                        />
                        <button
                            type="button"
                            onClick={() => { onEigene(zeile, eigene); setEigene(''); }}
                            disabled={gesperrt || !eigene.trim()}
                            title={sperrGrund}
                            className="px-3 py-1.5 text-xs font-bold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-40"
                        >
                            Hinzufügen
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
